/**
 * Template variable substitution engine.
 *
 * Supports two syntaxes (interchangeably) inside any template/section/checkbox text:
 *   - {variable_name}    — short-form, used for global fields like {first_name}
 *   - {{VARIABLE_NAME}}  — legacy long-form, used for template-defined fields
 *
 * Substitution is:
 *   - Reactive (runs on every render, never mutates source)
 *   - Safe (HTML-escapes user input by default to keep rich-text markup intact)
 *   - Resilient (missing values fall back to an empty string, never leave braces)
 */

/** Strongly-typed bag of global variables exposed to every contract. */
export interface TemplateVariables {
  first_name: string;
  last_name: string;
  /** Allow ad-hoc extra keys without losing strictness on the known ones. */
  [key: string]: string;
}

/** Keys that the form ALWAYS surfaces as global, mandatory fields. */
export const GLOBAL_VARIABLE_KEYS = ['first_name', 'last_name'] as const;
export type GlobalVariableKey = (typeof GLOBAL_VARIABLE_KEYS)[number];

const HTML_ESCAPES: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
};

function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (c) => HTML_ESCAPES[c]);
}

/**
 * Replace every `{key}` and `{{KEY}}` occurrence in `content` with the matching
 * value from `variables`. Missing keys collapse to an empty string so the final
 * output never contains stray placeholders.
 *
 * @param content     Source text (plain or HTML). Never mutated.
 * @param variables   Variable bag; keys are matched case-insensitively.
 * @param options     `escape: false` to skip HTML escaping (e.g. for plain-text PDFs).
 */
export function substituteVariables(
  content: string,
  variables: Record<string, string | undefined | null>,
  options: { escape?: boolean } = {},
): string {
  if (!content) return '';
  const escape = options.escape !== false;

  // Lowercase lookup map for case-insensitive matching.
  const lookup = new Map<string, string>();
  for (const [k, v] of Object.entries(variables)) {
    lookup.set(k.toLowerCase(), v == null ? '' : String(v));
  }

  const resolve = (rawKey: string): string => {
    const value = lookup.get(rawKey.toLowerCase()) ?? '';
    return escape ? escapeHtml(value) : value;
  };

  // Replace {{KEY}} first (longer pattern wins), then {key}.
  return content
    .replace(/\{\{\s*([\w.-]+)\s*\}\}/g, (_, key: string) => resolve(key))
    .replace(/\{\s*([\w.-]+)\s*\}/g, (_, key: string) => resolve(key));
}

/**
 * Extract every unique variable name referenced inside a piece of content.
 * Useful for previews ("variables used by this template") or validation.
 */
export function extractVariables(content: string): string[] {
  if (!content) return [];
  const found = new Set<string>();
  const re = /\{\{?\s*([\w.-]+)\s*\}?\}/g;
  let match: RegExpExecArray | null;
  while ((match = re.exec(content)) !== null) {
    found.add(match[1]);
  }
  return Array.from(found);
}

/**
 * Collect variables referenced across an arbitrary set of multilingual strings.
 */
export function collectTemplateVariables(contents: string[]): string[] {
  const all = new Set<string>();
  for (const c of contents) {
    for (const v of extractVariables(c)) all.add(v);
  }
  return Array.from(all);
}

/**
 * Build the system-derived variables (date, full name, practitioner) and merge
 * them with the form's `fieldValues`. Centralised so preview and PDF generation
 * always agree on what `{client.full_name}`, `{date_today}`, etc. resolve to.
 */
export function buildSubstitutionVars(input: {
  fieldValues: Record<string, string | undefined | null>;
  practitionerName?: string;
  language?: string;
  signedAt?: string | Date;
}): Record<string, string> {
  const { fieldValues, practitionerName, language = 'fr', signedAt } = input;
  const out: Record<string, string> = {};

  for (const [k, v] of Object.entries(fieldValues)) {
    out[k] = v == null ? '' : String(v);
  }

  const first = (out.first_name || '').trim();
  const last = (out.last_name || '').trim();
  const fullName = [first, last].filter(Boolean).join(' ');
  if (fullName) out['client.full_name'] = fullName;
  else if (!('client.full_name' in out)) out['client.full_name'] = '';

  const date = signedAt ? new Date(signedAt) : new Date();
  try {
    out.date_today = new Intl.DateTimeFormat(language, { dateStyle: 'long' }).format(date);
  } catch {
    out.date_today = date.toLocaleDateString();
  }

  out.practitioner_name = (practitionerName || '').trim();

  return out;
}
